import * as React from "react";
import { Link, useNavigate } from "react-router";
import {
  CircleCheck,
  ClipboardCheck,
  EllipsisVertical,
  Flag,
  Link2,
  OctagonAlert,
  Route as RouteIcon,
  ScanEye,
  Timer,
} from "lucide-react";
import {
  REVIEW_STATE_LABELS,
  visitTypeColor,
  visitTypeOrder,
  type Pagination as PageMeta,
  type ReviewState,
  type UserWithContext,
  type VisitStats,
  type VisitType,
  type VisitWithContext,
} from "@taskiq/shared";
import { PersonAvatar } from "@/components/ui/Avatar";
import { Badge, SeverityChip } from "@/components/ui/Badge";
import {
  DateRangeFilter,
  resolveRange,
  type DateRangeValue,
} from "@/components/ui/DateRangeFilter";
import { EmptyState, ErrorState } from "@/components/ui/Feedback";
import { FilterBar } from "@/components/ui/FilterBar";
import { FilterSelect, type FilterOption } from "@/components/ui/FilterSelect";
import { Identity } from "@/components/ui/Identity";
import { Menu, MenuItem, MenuSeparator } from "@/components/ui/Menu";
import { PageHeader } from "@/components/ui/PageHeader";
import { Pagination } from "@/components/ui/Pagination";
import { SearchInput } from "@/components/ui/SearchInput";
import { Stat } from "@/components/ui/Stat";
import { SkeletonRows, Table, Td, TdSpan, Th, Tr } from "@/components/ui/Table";
import { useToast } from "@/components/ui/toast-context";
import { api, apiErrorMessage } from "@/lib/api";
import { useApi } from "@/lib/useApi";
import { isDeletedUser } from "@/lib/users";
import { useAuth } from "@/lib/auth-context";
import { addDays, formatDayTimeIn, instantFrom, zoneOffset } from "@/lib/dates";
import { cn } from "@/lib/cn";
import { useTenancy } from "@/lib/tenancy";
import { LIST_PAGE_SIZE } from "@/lib/paging";
import { SEARCH_DEBOUNCE_MS, useDebounced } from "@/lib/useDebounced";

/**
 * The visit log: what was recorded, how it went, and what still needs a look.
 *
 * ── Every filter is the API's, not this page's ─────────────────────────────
 *
 * Date range, type, technician, review state and search all go into the query
 * string, so the table, the count and the pagination are answering the same
 * question. The alternative - fetch a page and filter it in the browser - draws
 * a total that disagrees with the rows under it as soon as the second page
 * exists.
 *
 * ── The tiles say only what the list can prove ─────────────────────────────
 *
 * Four figures over the WHOLE filtered set, from `GET /sites/:id/visit-stats`,
 * which aggregates every matching row under the caller's own RLS. The median
 * and the findings counts were removed from this page when they were computed
 * from one visible page of rows (decisions #40); the aggregate endpoint is
 * what brings them back honestly.
 *
 * ── Review state is the console's only write ───────────────────────────────
 *
 * Flagging and signing off are `PATCH /visits/:id { review_state }`. Starting and
 * completing a visit are the phone's, and this console does not offer them.
 */

const REVIEW_FILTERS: FilterOption[] = [
  { value: "", label: "Any review state" },
  { value: "open", label: REVIEW_STATE_LABELS.open },
  { value: "flagged", label: REVIEW_STATE_LABELS.flagged },
  { value: "reviewed", label: REVIEW_STATE_LABELS.reviewed },
];

interface VisitListResponse {
  visits: VisitWithContext[];
  pagination: PageMeta;
}

export function Visits() {
  const tenancy = useTenancy();
  const siteId = tenancy.activeSiteId;
  const { can } = useAuth();
  const toast = useToast();
  const timezone = tenancy.homeSite?.timezone ?? "UTC";
  const zoneLabel = zoneOffset(timezone);

  const [range, setRange] = React.useState<DateRangeValue>({ preset: "30d", from: "", to: "" });
  const [visitTypeId, setVisitTypeId] = React.useState("");
  const [technicianId, setTechnicianId] = React.useState("");
  const [review, setReview] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [page, setPage] = React.useState(1);
  // Debounced, because the SWR key is the URL: without this every keystroke is
  // a request and a cache entry.
  const term = useDebounced(search, SEARCH_DEBOUNCE_MS);

  // The preset has to be resolved to two days here. `DateRangeFilter` emits the
  // preset's *id* so "last 30 days" keeps meaning that tomorrow, which means a
  // consumer that reads `value.from` alone filters by nothing at all.
  const bounds = resolveRange(range, timezone);

  const filters = React.useMemo(() => {
    const params = new URLSearchParams();
    // The picker names two calendar days in the SITE's zone; the API takes
    // instants. So the boundaries are that zone's midnights, resolved through
    // the IANA database rather than by stapling a `Z` on - "2026-09-17" at a
    // Los Angeles yard begins at 07:00Z, and the naive version this replaces
    // filtered on UTC midnight, quietly counting the previous afternoon's
    // visits into the day and dropping the evening's out of it.
    //
    // The upper edge is the NEXT day's midnight, exclusive, so the last day of
    // the range is included whole - `23:59:59` would lose the final second and
    // is the sort of thing nobody notices until a visit falls in it.
    if (bounds.from) params.set("from", instantFrom(bounds.from, 0, 0, timezone));
    if (bounds.to) params.set("to", instantFrom(addDays(bounds.to, 1), 0, 0, timezone));
    if (visitTypeId) params.set("visit_type_id", visitTypeId);
    if (technicianId) params.set("user_id", technicianId);
    if (review) params.set("review_state", review);
    if (term.trim()) params.set("search", term.trim());
    return params;
  }, [bounds.from, bounds.to, timezone, visitTypeId, technicianId, review, term]);

  const listKey = React.useMemo(() => {
    if (!siteId) return null;
    const params = new URLSearchParams(filters);
    params.set("page", String(page));
    params.set("pageSize", String(LIST_PAGE_SIZE));
    return `/sites/${siteId}/visits?${params}`;
  }, [siteId, filters, page]);

  const list = useApi<VisitListResponse>(listKey);

  // The tiles: one aggregate call over the same filter set, no paging.
  const stats = useApi<{ stats: VisitStats }>(
    siteId ? `/sites/${siteId}/visit-stats?${filters}` : null
  );

  const types = useApi<{ visit_types: VisitType[] }>("/visit-types?is_active=true");
  const crew = useApi<{ users: UserWithContext[] }>(
    can("user.read") && siteId ? `/sites/${siteId}/users?pageSize=100` : null
  );

  const activeFilters = [visitTypeId, technicianId, review, term.trim()].filter(Boolean).length;

  React.useEffect(() => {
    setPage(1);
  }, [visitTypeId, technicianId, review, term, range.preset, range.from, range.to]);

  const typeOptions: FilterOption[] = [
    { value: "", label: "Any type" },
    ...[...(types.data?.visit_types ?? [])]
      .sort((a, b) => visitTypeOrder(a.code) - visitTypeOrder(b.code))
      .map((type) => ({ value: type.id, label: type.name })),
  ];

  const personOptions: FilterOption[] = [
    { value: "", label: "Anyone" },
    ...(crew.data?.users ?? []).map((person) => ({
      value: person.id,
      label: person.full_name ?? person.email,
      hint: person.email,
    })),
  ];

  const rows = list.data?.visits ?? [];
  const pagination = list.data?.pagination;

  async function setReviewState(visit: VisitWithContext, next: ReviewState) {
    try {
      await api(`/visits/${visit.id}`, { method: "PATCH", body: { review_state: next } });
      await Promise.all([list.mutate(), stats.mutate()]);
      toast.success(
        next === "flagged"
          ? "Flagged for review"
          : next === "reviewed"
            ? "Signed off"
            : "Flag cleared",
        { description: visit.appointment.reference ?? visit.appointment.title }
      );
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not update that visit"));
    }
  }

  async function copyLink(visit: VisitWithContext) {
    const url = `${window.location.origin}/app/visits/${visit.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied", { description: visit.appointment.reference ?? url });
    } catch {
      toast.info("Copy the link from the address bar", { description: url });
    }
  }

  return (
    <div className="page">
      <PageHeader
        title="Visits"
        description={
          <>
            Work recorded at {tenancy.homeSite?.name ?? "this site"}.{" "}
            <span className="subtle">{zoneLabel}</span>
          </>
        }
      />

      <FilterBar
        activeCount={activeFilters}
        onClear={() => {
          setVisitTypeId("");
          setTechnicianId("");
          setReview("");
          setSearch("");
        }}
        search={
          <SearchInput
            label="Search visits by appointment, reference or customer"
            placeholder="Search appointment, reference, customer"
            value={search}
            onChange={setSearch}
          />
        }
      >
        <DateRangeFilter value={range} onChange={setRange} timeZone={timezone} />
        <FilterSelect
          label="Filter by type"
          value={visitTypeId}
          active={visitTypeId !== ""}
          options={typeOptions}
          onChange={setVisitTypeId}
        />
        <FilterSelect
          label="Filter by technician"
          value={technicianId}
          active={technicianId !== ""}
          searchable
          options={personOptions}
          onChange={setTechnicianId}
        />
        <FilterSelect
          label="Filter by review state"
          value={review}
          active={review !== ""}
          options={REVIEW_FILTERS}
          onChange={setReview}
        />
      </FilterBar>

      <div className="stat-grid">
        <Stat label="Visits" icon={<RouteIcon />} value={stats.data?.stats.total ?? "-"} />
        <Stat
          label="Median assessment time"
          icon={<Timer />}
          value={
            stats.data?.stats.median_assessment_seconds == null
              ? "-"
              : `${stats.data.stats.median_assessment_seconds}s`
          }
          tooltip="Technician time from first scan to sign-off"
        />
        <Stat
          label="Open findings"
          icon={<ScanEye />}
          value={stats.data?.stats.open_findings ?? "-"}
          tooltip="Hazards nobody has acknowledged yet"
        />
        <Stat
          label="Stop-work findings"
          icon={<OctagonAlert />}
          value={stats.data?.stats.stop_work_findings ?? "-"}
          tooltip="Findings standing at stop: the analysis called it, or a technician refused to work under it"
        />
      </div>

      {list.error ? (
        <ErrorState error={list.error} onRetry={() => void list.mutate()} />
      ) : (
        <Table
          className="visit-table"
          minWidth="lg"
          head={
            <>
              <Th>Appointment</Th>
              <Th hideBelow="md">Technician</Th>
              <Th hideBelow="sm">Started</Th>
              <Th hideBelow="lg">Findings</Th>
              <Th>Review</Th>
              <Th className="agenda__chevron-col">
                <span className="sr-only">Actions</span>
              </Th>
            </>
          }
        >
          {list.isLoading && (
            <SkeletonRows
              columns={[
                { kind: "identity" },
                { kind: "identity", hideBelow: "md" },
                { kind: "text", hideBelow: "sm" },
                { kind: "badge", hideBelow: "lg" },
                { kind: "badge", width: "narrow" },
                { kind: "blank" },
              ]}
            />
          )}

          {!list.isLoading && rows.length === 0 && (
            <tr>
              <TdSpan colSpan={6}>
                <EmptyState
                  icon={<ClipboardCheck />}
                  title={activeFilters > 0 ? "No visits match that" : "No visits in this range"}
                  description={
                    activeFilters > 0
                      ? "Try a wider date range, a different technician, or clear the filters."
                      : "A visit appears here the moment a technician opens one on their phone."
                  }
                />
              </TdSpan>
            </tr>
          )}

          {!list.isLoading &&
            rows.map((visit) => (
              <VisitRow
                key={visit.id}
                visit={visit}
                timezone={timezone}
                canReview={can("visit.write")}
                onFlag={() =>
                  void setReviewState(visit, visit.review_state === "flagged" ? "open" : "flagged")
                }
                onReview={() => void setReviewState(visit, "reviewed")}
                onCopy={() => void copyLink(visit)}
              />
            ))}
        </Table>
      )}

      {pagination && <Pagination pagination={pagination} onPageChange={setPage} label="visits" />}
    </div>
  );
}

/* ────────────────────────────────── a row ───────────────────────────────── */

function VisitRow({
  visit,
  timezone,
  canReview,
  onFlag,
  onReview,
  onCopy,
}: {
  visit: VisitWithContext;
  timezone: string;
  canReview: boolean;
  onFlag: () => void;
  onReview: () => void;
  onCopy: () => void;
}) {
  const navigate = useNavigate();
  const rollup = visit.assessments_rollup;
  const worst = rollup?.findings.stop
    ? "stop"
    : rollup?.findings.caution
      ? "caution"
      : rollup?.findings.advisory
        ? "advisory"
        : null;

  return (
    <Tr>
      <Td>
        <Link to={`/app/visits/${visit.id}`} className="row-link">
          <Identity
            media={
              // The type's own hue, exactly as the schedule draws it. An
              // appointment should look the same on every screen.
              <span
                className={cn(
                  "agenda__marker",
                  `agenda__marker--${visitTypeColor(visit.visit_type?.code ?? "")}`
                )}
                aria-hidden
              />
            }
            title={visit.appointment.title}
            subtitle={visit.appointment.customer_name ?? visit.visit_type?.name}
          />
        </Link>
      </Td>

      <Td hideBelow="md">
        <Identity
          media={<PersonAvatar person={visit.technician} size="sm" />}
          title={visit.technician?.full_name ?? "Unknown"}
          subtitle={
            visit.technician && !isDeletedUser(visit.technician.full_name)
              ? visit.technician.email
              : undefined
          }
        />
      </Td>

      <Td hideBelow="sm">
        <span className="visit-when__stamp tnum">
          {formatDayTimeIn(visit.started_at, timezone)}
        </span>
      </Td>

      <Td hideBelow="lg">
        <span className="row-badges">
          {worst ? <SeverityChip severity={worst} /> : <span className="muted">-</span>}
        </span>
      </Td>

      <Td>
        <ReviewCell state={visit.review_state} />
      </Td>

      <Td align="right" className="agenda__chevron-col">
        <Menu
          trigger={
            <span className="row-menu">
              <EllipsisVertical aria-hidden />
              <span className="sr-only">
                Actions for {visit.appointment.reference ?? visit.appointment.title}
              </span>
            </span>
          }
        >
          {/* `navigate`, not a location assignment - the second reloads the
              whole console to reach a route the router already owns. */}
          <MenuItem icon={<ScanEye />} onClick={() => void navigate(`/app/visits/${visit.id}`)}>
            Open record
          </MenuItem>
          {canReview && (
            <>
              <MenuItem icon={<Flag />} onClick={onFlag}>
                {visit.review_state === "flagged" ? "Clear flag" : "Flag for review"}
              </MenuItem>
              <MenuItem
                icon={<CircleCheck />}
                disabled={visit.review_state === "reviewed"}
                onClick={onReview}
              >
                Mark reviewed
              </MenuItem>
            </>
          )}
          <MenuSeparator />
          <MenuItem icon={<Link2 />} onClick={onCopy}>
            Copy link
          </MenuItem>
        </Menu>
      </Td>
    </Tr>
  );
}

function ReviewCell({ state }: { state: ReviewState }) {
  if (state === "flagged") {
    return (
      <Badge tone="caution" icon={<Flag aria-hidden />}>
        {REVIEW_STATE_LABELS.flagged}
      </Badge>
    );
  }
  if (state === "reviewed") {
    return (
      <Badge tone="ok" icon={<CircleCheck aria-hidden />}>
        {REVIEW_STATE_LABELS.reviewed}
      </Badge>
    );
  }
  return <span className="muted">-</span>;
}
